// Handles actions dispatched for map collaboration (brushings).
import stringify from 'json-stringify-safe'

import { getCurrentTimestamp } from 'er2_web_utils'

import * as ids from '../constants/ids'
import * as errors from '../constants/errors'
import * as types from '../constants/action_types'
import { setValidationError } from './forms'
import { onSingleClick } from './ficus_map'
import * as wsActions from '../modules/websockets/client/actions/'
import * as wsTypes from '../modules/websockets/server/constants/action_types'
import { updateSettings } from './ficus_config'

// @flow
export const onFetchSessions = (isFetching: boolean, pollingId: number) =>
    ({ type: types.FETCH_SESSIONS, isFetching, pollingId })

// @flow
export const onReceiveSessions = (sessions: Array<object>) =>
    ({ type: types.RECEIVE_SESSIONS, sessions })

// @flow
export const onReceiveSessionsError = (resp: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_SESSIONS, resp })

// @flow
export const onFetchShareSession = (isFetching: boolean) =>
    ({ type: types.FETCH_SHARE_SESSION, isFetching })

// @flow
export const onReceiveShareSession = (sessions: Array<object>) =>
    ({ type: types.RECEIVE_SHARE_SESSION, sessions })

// @flow
export const onReceiveShareSessionError = (resp: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_SHARE_SESSION, resp })

// @flow
export const onFetchUnshareSession = (isFetching: boolean) =>
    ({ type: types.FETCH_UNSHARE_SESSION, isFetching })

// @flow
export const onReceiveUnshareSession = (success: boolean, token: string) =>
    ({ type: types.RECEIVE_UNSHARE_SESSION, success, token })

// @flow
export const onReceiveUnshareSessionError = (resp: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_UNSHARE_SESSION, resp })

// @flow
export const onFetchJoinSession = (isFetching: boolean) =>
    ({ type: types.FETCH_JOIN_SESSION, isFetching })

// @flow
export const onFetchSessionState = (isFetching: boolean) =>
    ({ type: types.FETCH_SESSION_STATE, isFetching })

// @flow
export const onReceiveJoinSession = (enabled: boolean, token: string) =>
    ({ type: types.RECEIVE_JOIN_SESSION, enabled, token })

// @flow
export const onReceiveJoinSessionError = (err: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_JOIN_SESSION, err })

// @flow
export const onReceiveSessionState = (state: object) =>
    ({ type: types.RECEIVE_SESSION_STATE, state })

// @flow
export const onReceiveSessionStateError = (resp: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_SESSION_STATE, resp })

// @flow
export const onFetchLeaveSession = (isFetching: boolean) =>
    ({ type: types.FETCH_LEAVE_SESSION, isFetching })

// @flow
export const onReceiveLeaveSession = (enabled: boolean, token: string) =>
    ({ type: types.RECEIVE_LEAVE_SESSION, enabled, token })

// @flow
export const onReceiveLeaveSessionError = (resp: object) =>
    ({ type: types.FETCH_ERROR, action: types.FETCH_LEAVE_SESSION, resp })

/**
 * @flow
 * When a session is successfully joined, join the room where socket communication happens
 * for this session
 */
export const emitJoinedSession = (token: string) => (dispatch, getState, { emit }) => {
    emit(wsTypes.WS_JOIN_ROOM, {
        applicationId: getState().meta.name,
        timestamp: getCurrentTimestamp(),
        roomId: token,
    })
}

/**
 * @flow
 * When a session is successfully left, leave the room where socket communication happens
 * for this session
 */
export const emitLeftSession = (token: string) => (dispatch, getState, { emit }) => {
    emit(wsTypes.WS_LEAVE_ROOM, {
        applicationId: getState().meta.name,
        timestamp: getCurrentTimestamp(),
        roomId: token,
    })
}

/**
 * @flow
 * Returns true if a session exists in a list of shared sessions
 * @param token: the state token
 * @param sessions: a list of shared sessions
 */
export const isSharedSession = (token, sessions) => sessions.some(session => session.token === token)

export const collaborationIsEnabled = props =>
    props.collaboration.sessions.items && (
        isSharedSession(props.token.value, props.collaboration.sessions.items)
    || isSharedSession(props.collaboration.sharedState.token, props.collaboration.sessions.items))

/**
 * @flow
 * Request to join a shared session
 */
export const onJoinSession = (token: string) => (dispatch, getState) => {
    let fetchError = false
    dispatch(onFetchJoinSession(true))
    // Disable layer caching to improve performance
    dispatch(updateSettings({ animationMode: false }))
    fetch(`/${getState().meta.name}/api/v1/sessions/${token}/`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: stringify({
            client: getState().token.value,
        }),
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        dispatch(onFetchJoinSession(false))
        if (fetchError) {
            dispatch(onReceiveJoinSessionError(json))
        } else {
            dispatch(emitJoinedSession(token))
            dispatch(onReceiveJoinSession(json.enabled, token))
        }
    }).catch((err) => {
        console.warn(err)
    })
}

/**
 * @flow
 * Request to leave a shared session
 */
export const onLeaveSession = (token: string) => (dispatch, getState) => {
    let fetchError = false
    dispatch(onFetchLeaveSession(true))
    dispatch(updateSettings({ animationMode: true }))
    fetch(`/${getState().meta.name}/api/v1/sessions/${token}/`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: stringify({
            '-client': getState().token.value,
        }),
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        dispatch(onFetchLeaveSession(false))
        if (fetchError) {
            dispatch(onReceiveLeaveSessionError(json))
        } else {
            dispatch(emitLeftSession(token))
            dispatch(onReceiveLeaveSession(json.enabled, token))
        }
    }).catch((err) => {
        console.warn(err)
    })
}

/**
 * @flow
 * Fetches all shared sessions
 */
export const fetchSessions = () => (dispatch, getState) => {
    let fetchError = false
    dispatch(onFetchSessions(true))
    fetch(`/${getState().meta.name}/api/v1/sessions/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        dispatch(onFetchSessions(false))
        if (fetchError) {
            dispatch(onReceiveSessionsError(json))
        } else {
            if (!getState().collaboration.enabled && isSharedSession(getState().token.value, json.items)) {
                // You previously shared a session. Join a new websocket room for
                // communications unique to this session
                dispatch(wsActions.joinRoom(getState().token.value))
                dispatch(onReceiveJoinSession(true, getState().token.value))
            }
            dispatch(onReceiveSessions(json.items))
        }
    }).catch((err) => {
        console.warn(err)
    })
}

/**
 * @flow
 * When a session is successfully shared, let everyone know
 */
export const emitSharedSession = (session: {
        token: string,
        name: string,
    }) => (dispatch, getState, { emit }) => {
    const payload = {
        applicationId: getState().meta.name,
        timestamp: getCurrentTimestamp(),
        operation: {
            name: types.OT_SHARE_SESSION,
            params: {
                ...session,
            },
        },
    }
    emit(wsTypes.WS_CLIENT_ACTION, payload)
}

/**
 * @flow
 * When a session is successfully shared, let everyone know
 */
export const emitUnsharedSession = (session: {
        token: string,
        name: string,
    }) => (dispatch, getState, { emit }) => {
    const payload = {
        applicationId: getState().meta.name,
        timestamp: getCurrentTimestamp(),
        operation: {
            name: types.OT_UNSHARE_SESSION,
            params: {
                ...session,
            },
        },
    }
    emit(wsTypes.WS_CLIENT_ACTION, payload)
}

/**
 * @flow
 * Share your current session with others
 */
export const onShareSession = () => (dispatch, getState) => {
    dispatch(updateSettings({ animationMode: false }))

    let fetchError = false
    if (!getState().collaboration.form.sessionName) {
        dispatch(setValidationError(
            ids.COLLABORATION_SESSION_NAME,
            errors.EMPTY_FIELD_REQUIRED,
        ))
    } else {
        const payload = {
            name: getState().collaboration.form.sessionName,
            token: getState().token.value,
        }
        // Reset any previous validation errors
        dispatch(setValidationError(
            ids.COLLABORATION_SESSION_NAME,
            errors.NO_ERROR,
        ))
        dispatch(onFetchShareSession(true))
        fetch(`/${getState().meta.name}/api/v1/sessions/?token=${getState().token.value}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: stringify(payload),
        }).then((response) => {
            fetchError = !response.ok
            return response.json()
        }).then((json) => {
            dispatch(onFetchShareSession(false))
            if (fetchError) {
                dispatch(onReceiveShareSessionError(json))
            } else {
                dispatch(emitSharedSession(payload))
                // Join a new websocket room for communications unique to this session
                dispatch(wsActions.joinRoom(getState().token.value))
                dispatch(onReceiveShareSession(json.items))
            }
        }).catch((err) => {
            console.warn(err)
        })
    }
}

/**
 * @flow
 * Unshare your current session with others
 */
export const onUnshareSession = () => (dispatch, getState) => {
    dispatch(updateSettings({ animationMode: true }))

    let fetchError = false
    dispatch(onFetchUnshareSession(true))
    fetch(`/${getState().meta.name}/api/v1/sessions/${getState().token.value}/`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        dispatch(onFetchUnshareSession(false))
        if (fetchError) {
            dispatch(onReceiveUnshareSessionError(json))
        } else {
            const payload = { token: getState().token.value }
            dispatch(emitUnsharedSession(payload))
            // Leave the websocket room for communications unique to this session
            dispatch(wsActions.leaveRoom(getState().token.value))
            dispatch(onReceiveUnshareSession(json.success, json.token))
            // Remove all shared mouse clicks from previous sessions
            dispatch(onSingleClick())
        }
    }).catch((err) => {
        console.warn(err)
    })
}

/**
 * @flow
 * Fetch the state of a target session.  Used for collaboration.
 */
export const fetchSessionState = token => (dispatch, getState) => {
    let fetchError = false
    dispatch(onFetchSessionState(true))
    fetch(`/${getState().meta.name}/api/v1/sessions/${token}/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then((response) => {
        fetchError = !response.ok
        return response.json()
    }).then((json) => {
        dispatch(onFetchSessionState(false))
        if (fetchError) {
            dispatch(onReceiveSessionStateError(json))
        } else {
            dispatch(onReceiveSessionState(json.state))
        }
    }).catch((err) => {
        console.warn(err)
    })
}
